import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, LinearProgress, Alert, Tooltip,
  List, ListItem, ListItemText, ListItemIcon, Divider, Collapse,
  FormControl, InputLabel, Select,
} from '@mui/material';
import {
  TrendingUp, Add, Edit, Delete, Close, CheckCircle, RadioButtonUnchecked,
  PlayArrow, ExpandMore, ExpandLess, Warning, Person, Group,
  School, Psychology, Star, WorkspacePremium,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

// ── API ───────────────────────────────────────────────────────────────────────

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || 'Error');
    return d;
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  draft: '#78909c', active: '#1565c0', completed: '#2e7d32', cancelled: '#b71c1c',
};
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', active: 'Active', completed: 'Completed', cancelled: 'Cancelled',
};

const ACTION_TYPE_ICONS: Record<string, React.ReactNode> = {
  training: <School fontSize="small" />,
  mentoring: <Person fontSize="small" />,
  self_study: <Psychology fontSize="small" />,
  on_the_job: <WorkspacePremium fontSize="small" />,
  certification: <Star fontSize="small" />,
  project: <TrendingUp fontSize="small" />,
  coaching: <Group fontSize="small" />,
  conference: <School fontSize="small" />,
  other: <RadioButtonUnchecked fontSize="small" />,
};

const ACTION_STATUS_COLORS: Record<string, string> = {
  not_started: '#90a4ae', in_progress: '#1565c0', completed: '#2e7d32', skipped: '#bdbdbd',
};
const ACTION_STATUS_LABELS: Record<string, string> = {
  not_started: 'Not Started', in_progress: 'In Progress', completed: 'Completed', skipped: 'Skipped',
};

const PERIODS = ['Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2', 'annual'];
const ACTION_TYPES = [
  'training', 'mentoring', 'self_study', 'on_the_job',
  'certification', 'project', 'coaching', 'conference', 'other',
];
const ACTION_STATUSES = ['not_started', 'in_progress', 'completed', 'skipped'];

// ── Types ─────────────────────────────────────────────────────────────────────

interface IdpAction {
  id: string; planId: string; title: string; description: string;
  type: string; status: string; dueDate: string; completedDate: string;
  skillId: string; notes: string; sortOrder: number;
}
interface IdpPlan {
  id: string; employeeId: string; title: string; objective: string;
  status: string; year: number; period: string; targetDate: string;
  reviewedById: string; managerNotes: string; progress: number;
  actions: IdpAction[];
  employee?: { firstName: string; lastName: string; department?: { name: string } };
  reviewedBy?: { firstName: string; lastName: string };
}
interface Stats {
  total: number; active: number; completed: number; draft: number;
  avgProgress: number; overdueActions: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function progressColor(p: number) {
  if (p >= 80) return '#2e7d32';
  if (p >= 50) return '#f57c00';
  return '#1565c0';
}

function StatCard({ label, value, icon, color }: any) {
  return (
    <Card sx={{ textAlign: 'center', p: 1 }}>
      <CardContent>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

// ── Plan Card ─────────────────────────────────────────────────────────────────

function PlanCard({ plan, isHR, onEdit, onActivate, onDelete, onAddAction, onUpdateAction, onDeleteAction }: {
  plan: IdpPlan; isHR: boolean;
  onEdit: () => void; onActivate: () => void; onDelete: () => void;
  onAddAction: () => void;
  onUpdateAction: (actionId: string, data: any) => void;
  onDeleteAction: (actionId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isOverdue = plan.targetDate && plan.targetDate < new Date().toISOString().split('T')[0]
    && plan.status === 'active';

  return (
    <Card sx={{ mb: 2, border: isOverdue ? '1px solid #c62828' : undefined }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={700}>{plan.title}</Typography>
              <Chip
                label={STATUS_LABELS[plan.status] ?? plan.status}
                size="small"
                sx={{ bgcolor: STATUS_COLORS[plan.status] ?? '#90a4ae', color: '#fff', fontWeight: 600 }}
              />
              <Chip label={`${plan.year} · ${plan.period}`} size="small" variant="outlined" />
              {isOverdue && <Chip label="Overdue" size="small" icon={<Warning />} color="error" />}
            </Box>
            {plan.employee && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {plan.employee.firstName} {plan.employee.lastName}
                {plan.employee.department ? ` · ${plan.employee.department.name}` : ''}
              </Typography>
            )}
            {plan.objective && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>{plan.objective}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            {plan.status === 'draft' && (isHR) && (
              <Tooltip title="Activate Plan">
                <IconButton size="small" color="primary" onClick={onActivate}>
                  <PlayArrow />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit}><Edit fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={onDelete}><Delete fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progress · {plan.actions?.length ?? 0} actions
            </Typography>
            <Typography variant="caption" fontWeight={700} color={progressColor(plan.progress)}>
              {plan.progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={plan.progress}
            sx={{
              height: 8, borderRadius: 4,
              bgcolor: '#e3f2fd',
              '& .MuiLinearProgress-bar': { bgcolor: progressColor(plan.progress) },
            }}
          />
        </Box>

        {/* Manager notes */}
        {plan.managerNotes && (
          <Alert severity="info" sx={{ mb: 1, py: 0 }}>
            <Typography variant="body2"><b>Manager note:</b> {plan.managerNotes}</Typography>
          </Alert>
        )}

        {/* Actions toggle */}
        {(plan.actions?.length > 0 || plan.status !== 'completed') && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Button
                size="small" startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setExpanded((e) => !e)}
              >
                {expanded ? 'Hide' : 'Show'} Actions ({plan.actions?.length ?? 0})
              </Button>
              {plan.status !== 'completed' && (
                <Button size="small" startIcon={<Add />} onClick={onAddAction}>Add Action</Button>
              )}
            </Box>

            <Collapse in={expanded}>
              <List dense sx={{ mt: 1 }}>
                {(plan.actions ?? []).sort((a, b) => a.sortOrder - b.sortOrder).map((action) => {
                  const isActionOverdue = action.dueDate &&
                    action.dueDate < new Date().toISOString().split('T')[0] &&
                    !['completed', 'skipped'].includes(action.status);
                  return (
                    <React.Fragment key={action.id}>
                      <ListItem
                        sx={{
                          bgcolor: isActionOverdue ? '#fff8e1' : undefined,
                          borderRadius: 1, mb: 0.5,
                        }}
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={action.status}
                                onChange={(e) => onUpdateAction(action.id, { status: e.target.value })}
                                sx={{ fontSize: '0.75rem', height: 28 }}
                              >
                                {ACTION_STATUSES.map((s) => (
                                  <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>
                                    {ACTION_STATUS_LABELS[s]}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <IconButton size="small" color="error"
                              onClick={() => onDeleteAction(action.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: ACTION_STATUS_COLORS[action.status] }}>
                          {action.status === 'completed'
                            ? <CheckCircle fontSize="small" />
                            : ACTION_TYPE_ICONS[action.type] ?? <RadioButtonUnchecked fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{ textDecoration: action.status === 'completed' ? 'line-through' : undefined }}
                              >
                                {action.title}
                              </Typography>
                              {isActionOverdue && <Warning sx={{ fontSize: 14, color: '#f57c00' }} />}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                              <Chip
                                label={action.type.replace(/_/g, ' ')}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: 16 }}
                              />
                              {action.dueDate && (
                                <Typography variant="caption" color={isActionOverdue ? 'error.main' : 'text.secondary'}>
                                  Due: {action.dueDate}
                                </Typography>
                              )}
                              {action.notes && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                  {action.notes}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  );
                })}
                {(!plan.actions || plan.actions.length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No actions yet. Add actions to track your development steps.
                  </Typography>
                )}
              </List>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function IDPPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = user?.role === 'manager';

  const [tab, setTab] = useState(0);
  const [myPlans, setMyPlans] = useState<IdpPlan[]>([]);
  const [allPlans, setAllPlans] = useState<IdpPlan[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters (all-plans tab)
  const [filterStatus, setFilterStatus] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  // Dialogs
  const [planDialog, setPlanDialog] = useState<Partial<IdpPlan> | null>(null);
  const [actionDialog, setActionDialog] = useState<{ planId: string; action?: Partial<IdpAction> } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [my, st] = await Promise.all([
        apiFetch('/api/idp/me'),
        isHR ? apiFetch('/api/idp/stats') : Promise.resolve(null),
      ]);
      setMyPlans(my);
      setStats(st);

      if (isHR || isManager) {
        const params = new URLSearchParams();
        if (filterStatus) params.set('status', filterStatus);
        if (filterYear) params.set('year', String(filterYear));
        const all = await apiFetch(`/api/idp?${params}`);
        setAllPlans(all);
      }
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isHR, isManager, filterStatus, filterYear]);

  useEffect(() => { load(); }, [load]);

  // ── Plan CRUD ──────────────────────────────────────────────────────────────

  const savePlan = async () => {
    if (!planDialog) return;
    try {
      if (planDialog.id) {
        await apiFetch(`/api/idp/${planDialog.id}`, {
          method: 'PATCH', body: JSON.stringify(planDialog),
        });
      } else {
        await apiFetch('/api/idp', {
          method: 'POST', body: JSON.stringify(planDialog),
        });
      }
      setPlanDialog(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const activatePlan = async (id: string) => {
    try {
      await apiFetch(`/api/idp/${id}/activate`, { method: 'POST' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const deletePlan = async (id: string) => {
    if (!window.confirm('Delete this IDP? This cannot be undone.')) return;
    try {
      await apiFetch(`/api/idp/${id}`, { method: 'DELETE' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Action CRUD ────────────────────────────────────────────────────────────

  const saveAction = async () => {
    if (!actionDialog) return;
    try {
      await apiFetch(`/api/idp/${actionDialog.planId}/actions`, {
        method: 'POST', body: JSON.stringify(actionDialog.action ?? {}),
      });
      setActionDialog(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const updateAction = async (actionId: string, data: any) => {
    try {
      await apiFetch(`/api/idp/actions/${actionId}`, {
        method: 'PATCH', body: JSON.stringify(data),
      });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const deleteAction = async (actionId: string) => {
    try {
      await apiFetch(`/api/idp/actions/${actionId}`, { method: 'DELETE' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Tabs ───────────────────────────────────────────────────────────────────

  const tabs = [
    { label: 'My Development Plans' },
    ...(isHR || isManager ? [{ label: isHR ? 'All Plans' : 'Team Plans' }] : []),
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp sx={{ fontSize: 32, color: '#0277bd' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Individual Development Plans</Typography>
            <Typography variant="body2" color="text.secondary">
              Set goals, plan actions, and track your growth
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ bgcolor: '#0277bd' }}
          onClick={() => setPlanDialog({
            year: new Date().getFullYear(),
            period: 'annual',
            status: 'draft',
          })}
        >
          New Plan
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats (HR only) */}
      {isHR && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Total Plans" value={stats.total} icon={<TrendingUp />} color="#0277bd" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Active" value={stats.active} icon={<PlayArrow />} color="#1565c0" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Completed" value={stats.completed} icon={<CheckCircle />} color="#2e7d32" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Drafts" value={stats.draft} icon={<Edit />} color="#78909c" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Avg Progress" value={`${stats.avgProgress}%`} icon={<TrendingUp />} color="#f57c00" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Overdue Actions" value={stats.overdueActions} icon={<Warning />} color="#c62828" />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {tabs.map((t, i) => <Tab key={i} label={t.label} />)}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── MY PLANS ── */}
      {tab === 0 && (
        <Box>
          {myPlans.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <TrendingUp sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography>No development plans yet. Create your first IDP to start growing!</Typography>
              <Button
                variant="contained" startIcon={<Add />} sx={{ mt: 2, bgcolor: '#0277bd' }}
                onClick={() => setPlanDialog({ year: new Date().getFullYear(), period: 'annual', status: 'draft' })}
              >
                Create My First Plan
              </Button>
            </Paper>
          ) : (
            myPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isHR={isHR}
                onEdit={() => setPlanDialog({ ...plan })}
                onActivate={() => activatePlan(plan.id)}
                onDelete={() => deletePlan(plan.id)}
                onAddAction={() => setActionDialog({ planId: plan.id, action: { type: 'training', status: 'not_started' } })}
                onUpdateAction={updateAction}
                onDeleteAction={deleteAction}
              />
            ))
          )}
        </Box>
      )}

      {/* ── ALL / TEAM PLANS ── */}
      {tab === 1 && (isHR || isManager) && (
        <Box>
          {/* Filters */}
          {isHR && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                select label="Status" size="small" sx={{ minWidth: 140 }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Year" size="small" sx={{ minWidth: 100 }}
                value={filterYear}
                onChange={(e) => setFilterYear(Number(e.target.value))}
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {allPlans.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No plans found.
            </Paper>
          ) : (
            allPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isHR={isHR}
                onEdit={() => setPlanDialog({ ...plan })}
                onActivate={() => activatePlan(plan.id)}
                onDelete={() => deletePlan(plan.id)}
                onAddAction={() => setActionDialog({ planId: plan.id, action: { type: 'training', status: 'not_started' } })}
                onUpdateAction={updateAction}
                onDeleteAction={deleteAction}
              />
            ))
          )}
        </Box>
      )}

      {/* ── PLAN DIALOG ── */}
      <Dialog open={!!planDialog} onClose={() => setPlanDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {planDialog?.id ? 'Edit Development Plan' : 'New Development Plan'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setPlanDialog(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Plan Title" fullWidth required
              value={planDialog?.title ?? ''}
              onChange={(e) => setPlanDialog((p) => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Leadership Development 2026"
            />
            <TextField
              label="Development Objective" fullWidth multiline rows={2}
              value={planDialog?.objective ?? ''}
              onChange={(e) => setPlanDialog((p) => ({ ...p, objective: e.target.value }))}
              placeholder="What do you want to achieve or improve?"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select label="Year" sx={{ flex: 1 }}
                value={planDialog?.year ?? new Date().getFullYear()}
                onChange={(e) => setPlanDialog((p) => ({ ...p, year: Number(e.target.value) }))}
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Period" sx={{ flex: 1 }}
                value={planDialog?.period ?? 'annual'}
                onChange={(e) => setPlanDialog((p) => ({ ...p, period: e.target.value }))}
              >
                {PERIODS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>
            <TextField
              label="Target Completion Date" type="date" fullWidth
              InputLabelProps={{ shrink: true }}
              value={planDialog?.targetDate ?? ''}
              onChange={(e) => setPlanDialog((p) => ({ ...p, targetDate: e.target.value }))}
            />
            {isHR && planDialog?.id && (
              <TextField
                label="Manager Notes" fullWidth multiline rows={2}
                value={planDialog?.managerNotes ?? ''}
                onChange={(e) => setPlanDialog((p) => ({ ...p, managerNotes: e.target.value }))}
                placeholder="Feedback or guidance for this plan..."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(null)}>Cancel</Button>
          <Button
            onClick={savePlan}
            variant="contained"
            sx={{ bgcolor: '#0277bd' }}
            disabled={!planDialog?.title || !planDialog?.year}
          >
            {planDialog?.id ? 'Save Changes' : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── ACTION DIALOG ── */}
      <Dialog open={!!actionDialog} onClose={() => setActionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Development Action
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setActionDialog(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Action Title" fullWidth required
              value={actionDialog?.action?.title ?? ''}
              onChange={(e) => setActionDialog((d) => d ? { ...d, action: { ...d.action, title: e.target.value } } : null)}
              placeholder="e.g. Complete leadership course on Coursera"
            />
            <TextField
              select label="Type" fullWidth
              value={actionDialog?.action?.type ?? 'training'}
              onChange={(e) => setActionDialog((d) => d ? { ...d, action: { ...d.action, type: e.target.value } } : null)}
            >
              {ACTION_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {ACTION_TYPE_ICONS[t]}
                    <span>{t.replace(/_/g, ' ')}</span>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description" fullWidth multiline rows={2}
              value={actionDialog?.action?.description ?? ''}
              onChange={(e) => setActionDialog((d) => d ? { ...d, action: { ...d.action, description: e.target.value } } : null)}
            />
            <TextField
              label="Due Date" type="date" fullWidth
              InputLabelProps={{ shrink: true }}
              value={actionDialog?.action?.dueDate ?? ''}
              onChange={(e) => setActionDialog((d) => d ? { ...d, action: { ...d.action, dueDate: e.target.value } } : null)}
            />
            <TextField
              label="Notes" fullWidth
              value={actionDialog?.action?.notes ?? ''}
              onChange={(e) => setActionDialog((d) => d ? { ...d, action: { ...d.action, notes: e.target.value } } : null)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button
            onClick={saveAction}
            variant="contained"
            sx={{ bgcolor: '#0277bd' }}
            disabled={!actionDialog?.action?.title}
          >
            Add Action
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
